import { useEffect, useState } from "react";
import { ApiService } from '../services/ApiService';
import type { CombineOnItem } from '../types/Api';

// ✅ Global in-memory cache
let cached: CombineOnItem[] | null = null;
let cachedPromise: Promise<void> | null = null;

export function getDefaultCombineOnStatic(): string {
  return cached?.[0]?.value ?? '';
}

// ✅ Preload function to be used in App.tsx
export async function preloadCombineOn() {
  if (cached) return;
  if (cachedPromise) return cachedPromise;

  cachedPromise = (async () => {
    try {
      const json = await ApiService.getCombineOnOptions();
      cached = json.combine_on.sort((a: CombineOnItem, b: CombineOnItem) => {
        if (a.category !== b.category) {
          return (a.category ?? '').localeCompare(b.category ?? '');
        }
        return a.label.localeCompare(b.label);
      });
    } finally {
      cachedPromise = null;
    }
  })();

  return cachedPromise;
}

export function useCombineOn() {
  const [combine_on, setCombineOn] = useState<CombineOnItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        if (cached) {
          setCombineOn(cached);
        } else {
          await preloadCombineOn();
          if (cached) setCombineOn(cached);
        }
      } catch (e: any) {
        setError(e.message ?? "Unknown error");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getCombineOnById = (value: string) =>
    cached?.find(op => op.value === value);

  const getCombineOnCategory = (value: string) =>
    getCombineOnById(value)?.category ?? null;

  return {
    combine_on,
    loading,
    error,
    getCombineOnById,
    getCombineOnCategory,
  };
}
